import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  CheckCircle,
  XCircle,
  Clock,
  RotateCcw,
  MoreHorizontal,
  ExternalLink,
  Video,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../../../components/ui/dropdown-menu';
import { TableCell, TableRow } from '../../../../../components/ui/table';
import reviewConstants from '../../../../../constants/review.constants';
import { BigHead } from '../../../../../lib/core.cjs.development';
import { cn } from '../../../../../lib/utils';
import apiService from '../../../../../services/api.service';
import ProgressBar from './ProgressBar';
import ReviewRecordLink from './ReviewRecordLink';
import StatisticDialog from './StatisticDialog';
import SummaryDialog from './SummaryDialog';

function HistoryTableRow({
  _id,
  userId,
  declined,
  confirmed,
  status,
  updatedAt,
  email,
  avatar,
  department,
  reviewGoal,
  newGoal,
  fetchData,
  currentPage,
  rowsPerPage,
  setIsLoading,
  revertedIds,
  reviewHistoryData,
  progress,
  departmentName,
  historySearchInput,
  historySort,
  template,
  skillStatistic,
  reviewRecordLinks,
  setErrorSnackbarText,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSummaryDialogOpened, setIsSummaryDialogOpened] = useState(false);
  const [declinedSummary, setDeclinedSummary] = useState('');
  const [isStatisticDialogOpened, setIsStatisticDialogOpened] = useState(false);
  const [reviewStatistic, setReviewStatistic] = useState({});
  const [hasApprovedReview, setHasApprovedReview] = useState(false);

  useEffect(() => {
    const hasApproved = reviewHistoryData.some(
      (data) =>
        data.status === reviewConstants.REVIEW_STATUSES.CONFIRMED && data.reviewGoal === reviewGoal,
    );
    setHasApprovedReview(hasApproved);
  }, [reviewHistoryData, reviewGoal]);

  const revertClick = async () => {
    try {
      setIsLoading(true);
      await apiService.head.processedReview(departmentName, _id);
      await fetchData(currentPage, rowsPerPage, historySearchInput, historySort);
      setIsLoading(false);
    } catch (err) {
      console.error(err);
      setIsLoading(false);
    }
  };

  const updateReviewLinks = async ({ depName, reviewId, links }) => {
    try {
      setIsLoading(true);
      await apiService.head.updateReviewRecordLinks({
        department: depName,
        reviewId,
        body: { reviewRecordLinks: links },
      });
      await fetchData(currentPage, rowsPerPage, historySearchInput, historySort);
      setIsLoading(false);
    } catch (err) {
      console.error(err);
      setIsLoading(false);
      setErrorSnackbarText(err.response.data?.messages?.join(', ') || 'Something went wrong');
    }
  };

  const summaryClick = async (id) => {
    try {
      const summary = await apiService.developer.getDeclinedSummaryById(id);
      setDeclinedSummary(summary);
      setIsSummaryDialogOpened(true);
    } catch (err) {
      console.error(err);
    }
  };

  const statisticClick = () => {
    try {
      const index = reviewHistoryData.findIndex((item) => item._id === _id);
      setReviewStatistic(skillStatistic[index]);
      setIsStatisticDialogOpened(true);
    } catch (error) {
      console.error(error);
    }
  };

  const reviewDate = new Date(updatedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const filteredReviewHistoryData = reviewHistoryData.filter((reviewData) =>
    revertedIds?.includes(reviewData._id),
  );

  const reviewIndex = reviewHistoryData.findIndex((item) => item._id === _id);
  const isStatisticDisabled = !skillStatistic[reviewIndex]?.tasksWithStatistic?.length;

  const isActionDisabled =
    status === reviewConstants.REVIEW_STATUSES.REVERTED ||
    status === reviewConstants.REVIEW_STATUSES.PROCESSED ||
    status === reviewConstants.REVIEW_STATUSES.CONFIRMED ||
    hasApprovedReview;

  const getStatusBadge = () => {
    switch (status) {
      case reviewConstants.REVIEW_STATUSES.CONFIRMED:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-500/10 text-green-600">
            <CheckCircle className="h-3 w-3" />
            Confirmed
          </span>
        );
      case reviewConstants.REVIEW_STATUSES.DECLINED:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-600">
            <XCircle className="h-3 w-3" />
            Declined
          </span>
        );
      case reviewConstants.REVIEW_STATUSES.REVERTED:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-500/10 text-gray-600">
            <RotateCcw className="h-3 w-3" />
            Reverted
          </span>
        );
      case reviewConstants.REVIEW_STATUSES.PROCESSED:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-600">
            <Clock className="h-3 w-3" />
            Processed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-500/10 text-gray-600">
            {status}
          </span>
        );
    }
  };

  return (
    <>
      <TableRow className={cn(isExpanded && 'border-b-0')}>
        <TableCell>
          <div className="w-10 h-10 rounded-full overflow-hidden bg-muted">
            <BigHead {...avatar} />
          </div>
        </TableCell>
        <TableCell className="font-medium">{email}</TableCell>
        <TableCell>{department}</TableCell>
        <TableCell>
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
            {reviewGoal}
          </span>
        </TableCell>
        <TableCell>
          <ReviewRecordLink
            reviewRecordLinks={reviewRecordLinks}
            updateReviewLinks={async (link) => {
              await updateReviewLinks({
                depName: departmentName,
                reviewId: _id,
                links: link,
              });
            }}
          />
        </TableCell>
        <TableCell>
          {newGoal && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-600">
              {newGoal}
            </span>
          )}
        </TableCell>
        <TableCell>{getStatusBadge()}</TableCell>
        <TableCell className="text-right">
          <span className="text-green-600 font-medium">{confirmed}</span>
        </TableCell>
        <TableCell className="text-right">
          <span className="text-red-600 font-medium">{declined}</span>
        </TableCell>
        <TableCell>
          <ProgressBar value={progress} />
        </TableCell>
        <TableCell className="text-muted-foreground">{reviewDate}</TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem disabled={isActionDisabled} onClick={() => summaryClick(userId)}>
                  Summary
                </DropdownMenuItem>
                <DropdownMenuItem disabled={isActionDisabled} onClick={revertClick}>
                  Revert
                </DropdownMenuItem>
                <DropdownMenuItem disabled={isStatisticDisabled} onClick={statisticClick}>
                  Statistic
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {status !== reviewConstants.REVIEW_STATUSES.REVERTED && revertedIds && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => setIsExpanded(!isExpanded)}
              >
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>

      {/* Expanded reverted rows */}
      {isExpanded &&
        filteredReviewHistoryData.map((data) => (
          <TableRow key={data._id} className="bg-muted/30">
            <TableCell>
              <div className="w-10 h-10 rounded-full overflow-hidden bg-muted">
                <BigHead {...data.avatar} />
              </div>
            </TableCell>
            <TableCell className="font-medium">{data.email}</TableCell>
            <TableCell>{data.department}</TableCell>
            <TableCell>
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                {data.reviewGoal}
              </span>
            </TableCell>
            <TableCell>
              <ReviewRecordLink
                reviewRecordLinks={data.reviewRecordLinks}
                updateReviewLinks={(link) => {
                  updateReviewLinks({
                    depName: data.departmentName,
                    reviewId: data._id,
                    links: link,
                  });
                }}
              />
            </TableCell>
            <TableCell>
              {data.newGoal && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-500/10 text-blue-600">
                  {data.newGoal}
                </span>
              )}
            </TableCell>
            <TableCell>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-500/10 text-gray-600">
                <RotateCcw className="h-3 w-3" />
                Reverted
              </span>
            </TableCell>
            <TableCell className="text-right">
              <span className="text-green-600 font-medium">{data.confirmed}</span>
            </TableCell>
            <TableCell className="text-right">
              <span className="text-red-600 font-medium">{data.declined}</span>
            </TableCell>
            <TableCell>
              <ProgressBar value={data.progress} />
            </TableCell>
            <TableCell className="text-muted-foreground">
              {new Date(data.updatedAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
              })}
            </TableCell>
            <TableCell />
          </TableRow>
        ))}

      {isSummaryDialogOpened && (
        <SummaryDialog
          isOpen={isSummaryDialogOpened}
          declinedSummary={declinedSummary}
          onClose={() => setIsSummaryDialogOpened(false)}
        />
      )}

      {isStatisticDialogOpened && (
        <StatisticDialog
          isOpen={isStatisticDialogOpened}
          onClose={() => setIsStatisticDialogOpened(false)}
          template={template}
          reviewStatistic={reviewStatistic}
        />
      )}
    </>
  );
}

HistoryTableRow.propTypes = {
  _id: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  declined: PropTypes.number.isRequired,
  confirmed: PropTypes.number.isRequired,
  status: PropTypes.string.isRequired,
  updatedAt: PropTypes.string.isRequired,
  email: PropTypes.string.isRequired,
  avatar: PropTypes.shape({}).isRequired,
  department: PropTypes.string.isRequired,
  departmentName: PropTypes.string.isRequired,
  reviewGoal: PropTypes.string.isRequired,
  newGoal: PropTypes.string,
  progress: PropTypes.number.isRequired,
  fetchData: PropTypes.func.isRequired,
  currentPage: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  revertedIds: PropTypes.arrayOf(PropTypes.string),
  reviewHistoryData: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  historySearchInput: PropTypes.string.isRequired,
  historySort: PropTypes.string.isRequired,
  template: PropTypes.string.isRequired,
  skillStatistic: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
  reviewRecordLinks: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
  setErrorSnackbarText: PropTypes.func.isRequired,
};

HistoryTableRow.defaultProps = {
  revertedIds: null,
  newGoal: '',
  reviewRecordLinks: null,
};

export default HistoryTableRow;
